import type { CSSProperties } from 'react';
import { NavLink } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, PinOff } from 'lucide-react';
import {
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuAction,
} from '@/components/ui/sidebar';
import type { FeatureItem } from './FeatureNavigator';
import type { FeatureDecorator } from './use-feature-decorators';

interface SortablePinnedItemProps {
	item: FeatureItem;
	decorator?: FeatureDecorator;
	isActive: boolean;
	onUnpin: (id: string) => void;
}

export function SortablePinnedItem({
	item,
	decorator,
	isActive,
	onUnpin,
}: SortablePinnedItemProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id });

	const style: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<SidebarMenuItem ref={setNodeRef} style={style}>
			<SidebarMenuButton
				asChild
				tooltip={item.title}
				isActive={isActive}
				className="pr-12 max-md:!pr-24"
			>
				<NavLink to={item.url} data-onboarding={item.id === 'campaigns' ? 'campaigns' : undefined}>
					<item.icon className="size-4" />
					<div className="flex items-center justify-between w-full min-w-0">
						<div className="flex items-center gap-1.5 min-w-0">
							<span className="truncate">{item.title}</span>
							{decorator?.hasUnseen && (
								<span className="flex h-2 w-2 shrink-0 rounded-full bg-primary" />
							)}
						</div>
						{decorator?.count && decorator.count > 0 ? (
							<span className="ml-auto pl-2 shrink-0 text-xs">
								{decorator.count}
							</span>
						) : null}
					</div>
				</NavLink>
			</SidebarMenuButton>
			<SidebarMenuAction
				showOnHover
				onClick={() => onUnpin(item.id)}
				title="Unpin"
				aria-label={`Unpin ${item.title}`}
				className="right-7 hover:text-muted-foreground/50 max-md:right-12"
			>
				<PinOff className="size-2 text-muted-foreground/50 hover:text-muted-foreground max-md:!size-4" />
			</SidebarMenuAction>
			<SidebarMenuAction
				showOnHover
				aria-label={`Reorder ${item.title}`}
				title="Drag to reorder"
				className="cursor-grab touch-none active:cursor-grabbing hover:text-muted-foreground/50"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="size-3.5 text-muted-foreground/50 hover:text-muted-foreground max-md:!size-[18px]" />
			</SidebarMenuAction>
		</SidebarMenuItem>
	);
}
